// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { Columns } from "@phosphor-icons/react";
import { Button } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCompareBar } from "./AskV2ReviewCompareBar";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { type ReviewVersionOption, VersionPicker } from "./VersionPicker";

type AskV2ReviewArtworkHeaderProps = {
  review: AskPunttReview;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  versionOptions: readonly ReviewVersionOption[];
  onSwitchReview: (reviewId: string) => void;
  /**
   * The left column's pane switcher. Left out of the compare bar: the same
   * tabs stay under the chat field.
   */
  paneTabs: ReactNode;
  /** The viewer's rotate and zoom controls. */
  viewerControls: ReactNode;
};

/**
 * The row over the artwork column: the version picker, Compare and the
 * viewer controls, or while comparing, the one compare bar in their place.
 */
export function AskV2ReviewArtworkHeader({
  review,
  compare,
  versionOptions,
  onSwitchReview,
  paneTabs,
  viewerControls,
}: AskV2ReviewArtworkHeaderProps) {
  const { t } = useTranslation("ui");
  const { canCompare, isComparing, versionMark } = compare;
  return (
    <div
      // why: h-12 is PANE_HEADER_HEIGHT_CLASS, matching the left column's
      // Back header so both borders sit on one line; ask-v2 cannot
      // import the V1 module that exports it
      className="flex h-12 shrink-0 items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4"
      data-testid="ask-v2-review-artwork-header"
    >
      {isComparing ? (
        <AskV2ReviewCompareBar
          comparedReview={compare.comparedQuery.data?.review}
          comparedOptions={compare.compareOptions}
          comparedMark={versionMark(compare.compareReviewId)}
          onSelectCompared={compare.setCompareChoice}
          openReview={review}
          openOptions={versionOptions}
          openMark={versionMark(review.reviewId)}
          onSelectOpen={onSwitchReview}
          openIsEarlier={compare.openIsEarlier}
          syncPanes={compare.syncPanes}
          onSyncPanesChange={compare.setSyncPanes}
          viewerControls={viewerControls}
          onExit={() => compare.setComparing(false)}
        />
      ) : (
        <>
          <VersionPicker
            review={review}
            options={versionOptions}
            onSelect={onSwitchReview}
            mark={versionMark(review.reviewId)}
          />
          {canCompare ? (
            <Button
              variant="soft"
              color="gray"
              size="1"
              data-testid="ask-v2-review-compare-toggle"
              aria-label={t("askV2.review.compareToggleLabel")}
              onClick={() => compare.setComparing(true)}
            >
              <Columns size={14} weight="bold" aria-hidden="true" />
              {t("askV2.review.compareToggle")}
            </Button>
          ) : null}
          {paneTabs}
          {viewerControls}
        </>
      )}
    </div>
  );
}
